import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Stack,
  Paper,
  Autocomplete,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  IconButton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import emailjs from '@emailjs/browser';
import { recurringAPI, surveyAPI, userAPI } from '../services/api';

const weekdays = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const OneToOnes: React.FC = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [assignments, setAssignments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Form state
  const [selUser, setSelUser] = useState<any | null>(null);
  const [selTemplate, setSelTemplate] = useState<any | null>(null);
  const [intervalWeeks, setIntervalWeeks] = useState<number>(1);
  const [weekday, setWeekday] = useState<number>(1);
  const [timeOfDay, setTimeOfDay] = useState<string>('09:00');
  const [timezone, setTimezone] = useState<string>(() => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
  const [startDate, setStartDate] = useState<string>(() => new Date().toISOString().substring(0, 10));
  const [endDate, setEndDate] = useState<string>('');
  const [saving, setSaving] = useState(false);

  const tzHint = useMemo(() => `Detected: ${Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'}`, []);

  const loadAll = async () => {
    setLoading(true);
    setError('');
    const issues: string[] = [];
    // Users
    try {
      const u = await userAPI.getUsers();
      setUsers(u.data || []);
    } catch (e: any) {
      issues.push(e?.response?.data?.message || 'Users');
    }
    // Templates (fallback via surveys if needed)
    try {
      const t = await surveyAPI.getTemplates();
      setTemplates(t.data || []);
    } catch (e1: any) {
      try {
        const all = await surveyAPI.getSurveys();
        const tpl = (all.data || []).filter((s: any) => !!s.isTemplate);
        setTemplates(tpl);
        issues.push('Templates endpoint unavailable, used fallback');
      } catch (e2: any) {
        issues.push(e1?.response?.data?.message || 'Templates');
      }
    }
    // Recurring assignments (non-fatal)
    try {
      const r = await recurringAPI.list();
      setAssignments(r.data || []);
    } catch (e: any) {
      issues.push('Recurring');
    }
    if (issues.length) setError(`Failed to load: ${issues.join(', ')}`);
    setLoading(false);
  };

  useEffect(() => {
    loadAll();
  }, []);

  const formatDateTime = (iso?: string) => iso ? new Date(iso).toLocaleString() : '—';

  const handleCreate = async () => {
    if (!selUser || !selTemplate) return;
    setSaving(true);
    setError('');
    try {
      await recurringAPI.create({
        userId: selUser._id,
        surveyTemplateId: selTemplate._id,
        intervalWeeks: Math.max(1, Number(intervalWeeks || 1)),
        weekday,
        timeOfDay,
        timezone,
        startDate: `${startDate}T00:00:00.000Z`,
        endDate: endDate ? `${endDate}T23:59:59.999Z` : undefined,
      });
      // reset minimal
      setIntervalWeeks(1);
      setWeekday(1);
      setTimeOfDay('09:00');
      setEndDate('');
      await loadAll();
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to create recurring assignment');
    } finally {
      setSaving(false);
    }
  };

  const handleRunNow = async (row: any) => {
    try {
      const res = await recurringAPI.runNow(row._id);
      // Optional: send EmailJS notification to employee
      const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID as string;
      const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID as string;
      const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY as string;
      if (SERVICE_ID && TEMPLATE_ID && PUBLIC_KEY) {
        if (!(window as any).__emailjs_inited) {
          emailjs.init(PUBLIC_KEY);
          (window as any).__emailjs_inited = true;
        }
        const employee = users.find((u) => u._id === row.userId?._id || row.userId);
        const toEmail = employee?.email || '';
        const toName = employee?.name || 'Team member';
        const surveyId = res.data?.surveyId;
        const appUrl = window.location.origin;
        const link = surveyId ? `${appUrl}/surveys/${surveyId}/take` : `${appUrl}/dashboard`;
        await emailjs.send(
          SERVICE_ID,
          TEMPLATE_ID,
          {
            to_email: toEmail,
            to_name: toName,
            subject: `New 1-2-1 survey: ${row?.surveyTemplateId?.title || 'Weekly check-in'}`,
            message: `Hi ${toName}, a new 1-2-1 survey is ready for you.\n\nOpen: ${link}`,
          }
        );
      }
    } catch (e) {
      // ignore email errors, but reload assignments
    } finally {
      await loadAll();
    }
  };

  const handlePauseResume = async (row: any) => {
    const next = row.status === 'active' ? 'paused' : 'active';
    try {
      await recurringAPI.update(row._id, { status: next });
      await loadAll();
    } catch (e) {}
  };

  const handleDelete = async (row: any) => {
    if (!window.confirm('Delete this recurring assignment?')) return;
    try {
      await recurringAPI.delete(row._id);
      await loadAll();
    } catch (e) {}
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 3 }}>1-2-1s</Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Create recurring 1-2-1</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Autocomplete
              options={users}
              getOptionLabel={(o: any) => o.name || o.email}
              value={selUser}
              onChange={(_, v) => setSelUser(v)}
              renderInput={(params) => <TextField {...params} label="Employee" placeholder="Select employee" />}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Autocomplete
              options={templates}
              getOptionLabel={(o: any) => o.title}
              value={selTemplate}
              onChange={(_, v) => setSelTemplate(v)}
              renderInput={(params) => <TextField {...params} label="Survey template" placeholder="Select template" />}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField type="number" label="Every N weeks" value={intervalWeeks} onChange={(e) => setIntervalWeeks(Math.max(1, Number(e.target.value || 1)))} inputProps={{ min: 1 }} />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField select label="Weekday" value={weekday} onChange={(e) => setWeekday(Number(e.target.value))}>
              {weekdays.map((d) => (<MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>))}
            </TextField>
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField type="time" label="Time" value={timeOfDay} onChange={(e) => setTimeOfDay(e.target.value)} />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField label="Timezone" value={timezone} onChange={(e) => setTimezone(e.target.value)} helperText={tzHint} />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField type="date" label="Start date" value={startDate} onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField type="date" label="End date" value={endDate} onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} md={'auto'}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ height: '100%' }}>
              <Button variant="contained" onClick={handleCreate} disabled={!selUser || !selTemplate || saving}>Create</Button>
              {error && <Typography variant="body2" color="error">{error}</Typography>}
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6">Recurring assignments</Typography>
          <Button size="small" startIcon={<RestartAltIcon />} onClick={() => recurringAPI.runDue().then(loadAll)}>Run due</Button>
        </Stack>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Employee</TableCell>
                <TableCell>Template</TableCell>
                <TableCell>Interval</TableCell>
                <TableCell>Schedule</TableCell>
                <TableCell>Next run</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(assignments || []).map((r: any) => (
                <TableRow key={r._id} hover>
                  <TableCell>{r.userId?.name || r.userId?.email || '—'}</TableCell>
                  <TableCell>{r.surveyTemplateId?.title || '—'}</TableCell>
                  <TableCell>Every {r.intervalWeeks || 1} week(s)</TableCell>
                  <TableCell>{weekdays.find(w => w.value === r.weekday)?.label || r.weekday} @ {r.timeOfDay} ({r.timezone})</TableCell>
                  <TableCell>{formatDateTime(r.nextRunAt)}</TableCell>
                  <TableCell>
                    <Chip size="small" label={r.status} color={r.status === 'active' ? 'success' : (r.status === 'paused' ? 'default' : 'warning')} variant="outlined" />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleRunNow(r)} title="Run now"><PlayArrowIcon /></IconButton>
                    <IconButton size="small" onClick={() => handlePauseResume(r)} title={r.status === 'active' ? 'Pause' : 'Resume'}>
                      {r.status === 'active' ? <PauseIcon /> : <PlayArrowIcon />}
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(r)} title="Delete"><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Paper>
    </Container>
  );
};

export default OneToOnes;
